import React, { useState, useEffect } from 'react';
import { 
  Download, FileDown, ChevronDown, ChevronUp,
  Search, X, BadgeCheck, XCircle, MessageSquare, User,
  ClipboardList, FileText, Award
} from 'lucide-react';
import apiService from '../services/api';
import { FormResponse, Answer } from '../types/api';
import { downloadFormResponsesAsPDF } from '../utils/downloadUtils';

interface ResponseCardProps {
  response: FormResponse;
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: (response: FormResponse) => void;
}

const ResponseCard: React.FC<ResponseCardProps> = ({ 
  response, isExpanded, onToggle, onDownload
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score?: number) => {
    return 'from-red-500 to-rose-600';
  };

  return (
    <div className="user-card">
      {/* Card Header */}
      <div className="user-card-header" onClick={onToggle}>
        <div className="user-card-left">
          <div className={`user-avatar-large bg-gradient-to-br ${getScoreColor(response.score)}`}>
            <MessageSquare size={24} />
          </div>
          <div className="user-card-info">
            <div className="user-card-name">Response #{response._id?.substring(0, 8)}</div>
            <div className="user-card-meta">
              <span className="user-card-id">
                <User size={12} />
                {response.sbu_id}
              </span>
              <span className="user-card-email">
                <MessageSquare size={12} />
                {response.answers?.length || 0} answers
              </span>
            </div>
          </div>
        </div>
        
        <div className="user-card-right">
          <div className="user-card-badges">
            {response.score !== undefined && (
              <span className={`user-badge ${response.score >= 60 ? 'badge-active' : 'badge-blocked'}`}>
                <Award size={12} /> Score: {response.score}
              </span>
            )}
            <span className="user-badge badge-answers">
              {response.answers?.length || 0} Answers
            </span>
          </div>
          <button className="expand-btn" aria-label="Toggle details">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`user-card-body ${isExpanded ? 'expanded' : ''}`}>
        <div className="user-details-grid">
          {/* Response Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <FileText size={16} />
              Response Details
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Response ID</span>
                <span className="detail-value font-mono text-xs">{response._id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Form ID</span>
                <span className="detail-value font-mono text-xs">{response.form_id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">User ID (SBU)</span>
                <span className="detail-value font-mono">{response.sbu_id}</span>
              </div>
            </div>
          </div>

          {/* Score & Stats */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Award size={16} />
              Score & Stats
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Score</span>
                <span className={`detail-value text-lg font-bold ${
                  response.score !== undefined 
                    ? response.score >= 60 ? 'text-green-600' : 'text-red-600'
                    : 'text-gray-400'
                }`}>
                  {response.score !== undefined ? response.score : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Total Answers</span>
                <span className="detail-value">{response.answers?.length || 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Submitted</span>
                <span className="detail-value">{formatDate(response.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Answers Preview */}
          <div className="detail-section" style={{ gridColumn: 'span 2' }}>
            <h4 className="detail-section-title">
              <MessageSquare size={16} />
              Answers ({response.answers?.length || 0})
            </h4>
            <div className="answers-list">
              {response.answers?.slice(0, 5).map((answer: Answer, idx: number) => (
                <div key={idx} className="answer-item">
                  <div className="answer-question">
                    <span className="question-number">{idx + 1}</span>
                    {answer.question}
                  </div>
                  <div className="answer-content">
                    <span className="answer-type-badge">{answer.answer_type}</span>
                    <span className="answer-text">{answer.answer_text || 'No answer'}</span>
                  </div>
                </div>
              ))}
              {(response.answers?.length || 0) > 5 && (
                <div className="question-more">
                  +{response.answers!.length - 5} more answers
                </div>
              )}
              {(!response.answers || response.answers.length === 0) && (
                <div className="question-empty">No answers recorded</div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="user-card-actions">
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(response); }}
            className="action-btn action-download"
            title="Download as PDF"
          >
            <Download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

const FormResponses: React.FC = () => {
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedResponses, setExpandedResponses] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchResponses();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllFormResponses();
      setResponses(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch form responses');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (responseId: string) => {
    setExpandedResponses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(responseId)) {
        newSet.delete(responseId);
      } else {
        newSet.add(responseId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedResponses(new Set(filteredResponses.map(r => r._id!)));
  };

  const collapseAll = () => {
    setExpandedResponses(new Set());
  };

  const handleDownloadAllPDF = async () => {
    try {
      await downloadFormResponsesAsPDF(responses, 'form-responses');
      setSuccess('All responses exported successfully!');
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download form responses. Please try again.');
    }
  };

  const handleDownloadIndividual = async (response: FormResponse) => {
    try {
      await downloadFormResponsesAsPDF([response], `form-response-${response._id}`);
      setSuccess('Response downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download form response. Please try again.');
    }
  };

  // Filter responses
  const filteredResponses = responses.filter(response => {
    return searchQuery === '' || 
      response._id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      response.sbu_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      response.form_id.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h2 className="users-title">Form Responses</h2>
          <p className="users-subtitle">View all form submissions • {responses.length} total responses</p>
        </div>
        {responses.length > 0 && (
          <button onClick={handleDownloadAllPDF} className="btn btn-primary">
            <FileDown size={16} />
            Export All as PDF
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="message message-error">
          <XCircle size={18} />
          {error}
          <button onClick={() => setError(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="message message-success">
          <BadgeCheck size={18} />
          {success}
          <button onClick={() => setSuccess(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Filters & Search */}
      <div className="users-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by response ID, user ID, or form ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filters-group">
          <div className="expand-controls">
            <button onClick={expandAll} className="expand-btn-small" title="Expand All">
              <ChevronDown size={16} /> Expand All
            </button>
            <button onClick={collapseAll} className="expand-btn-small" title="Collapse All">
              <ChevronUp size={16} /> Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="results-count">
        Showing {filteredResponses.length} of {responses.length} responses
      </div>

      {/* Response Cards */}
      <div className="users-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading responses...</p>
          </div>
        ) : filteredResponses.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} className="text-gray-300" />
            <h3>No responses found</h3>
            <p>Try adjusting your search</p>
          </div>
        ) : (
          filteredResponses.map(response => (
            <ResponseCard
              key={response._id}
              response={response}
              isExpanded={expandedResponses.has(response._id!)}
              onToggle={() => toggleExpanded(response._id!)}
              onDownload={handleDownloadIndividual}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default FormResponses;
